import type { DailyRatesData } from '@/features/reports/context/report-data-context';
import type { ProcessedMeetingNotesData, SimplifiedNotesPayload } from '../types/processed-data';
import {
  transformGroupData,
  transformNewGroupsData,
  transformRmRecsData,
} from './data-transformers';
import {
  calculateKeyMetricsAlerts,
  isMondayMeeting,
  type KeyMetric,
} from './key-metrics-calculator';
import { analyzeRateShopData, processRateShopForNotes } from './rate-shop-analyzer';

/**
 * Central preprocessor that coordinates all data calculations and formatting
 * This removes ALL math and formatting logic from LLM prompts
 */
export function preprocessMeetingNotesData(
  rawData: {
    keyMetrics?: KeyMetric[];
    fiveDayRatesData?: DailyRatesData;
    rmRecsData?: any;
    groupData?: any;
    newGroupKeys?: string[];
    existingNotes?: string;
    brandCode?: string;
    userId?: string;
    meetingDate?: string;
  },
  meetingType?: string,
): ProcessedMeetingNotesData {
  const processed: ProcessedMeetingNotesData = {
    existingNotes: rawData.existingNotes,
    brandCode: rawData.brandCode,
    userId: rawData.userId,
  };

  // Process key metrics - calculate alerts and format messages
  // Monday meetings report Δ3D rather than Δ1D, so the label has to match the
  // column the caller put in KeyMetric.delta_1day.
  if (rawData.keyMetrics && rawData.keyMetrics.length > 0) {
    processed.keyMetrics = calculateKeyMetricsAlerts(
      rawData.keyMetrics,
      isMondayMeeting(rawData.meetingDate),
    );
  }

  // Process rate shop data - analyze and format summary items
  // Pass meetingType to prioritize appropriate rate changes (1-day for DAILY, 7-day for WEEKLY)
  if (rawData.fiveDayRatesData) {
    const rateAnalysis = analyzeRateShopData(rawData.fiveDayRatesData as any, meetingType);
    processed.rateShop = processRateShopForNotes(rateAnalysis);
  }

  // Process RM recommendations - format summary text
  if (rawData.rmRecsData) {
    processed.rmRecs = transformRmRecsData(rawData.rmRecsData);
  }

  // Process group data - format summary items
  if (rawData.groupData) {
    processed.groups = transformGroupData(rawData.groupData);
  }

  // Process new groups - format summary items for newly added groups
  if (rawData.groupData && rawData.meetingDate && rawData.newGroupKeys) {
    processed.newGroups = transformNewGroupsData(
      rawData.groupData,
      rawData.meetingDate,
      rawData.newGroupKeys,
    );
  }

  return processed;
}

/**
 * Creates simplified payload structure for LLM with pre-calculated sections
 * LLM only needs to arrange pre-formatted content, no calculations needed
 */
export function createSimplifiedPayload(
  processedData: ProcessedMeetingNotesData,
  meetingType: string,
): SimplifiedNotesPayload {
  const payload: SimplifiedNotesPayload = {
    sections: {},
    meetingType,
    existingNotes: processedData.existingNotes,
  };

  // Key metrics section
  if (processedData.keyMetrics) {
    const hasOooAlert = processedData.keyMetrics.ooo.hasAlert;
    const hasDeltaAlert = processedData.keyMetrics.delta_1day.hasAlert;

    if (hasOooAlert || hasDeltaAlert) {
      payload.sections.keyMetrics = {
        hasAlerts: true,
        oooMessage: hasOooAlert ? processedData.keyMetrics.ooo.alertMessage : undefined,
        deltaMessage: hasDeltaAlert ? processedData.keyMetrics.delta_1day.alertMessage : undefined,
      };
    }
  }

  // Rate shop section
  if (processedData.rateShop?.hasSignificantChanges) {
    payload.sections.rateShop = {
      hasChanges: true,
      summaryItems: processedData.rateShop.formattedSummaryItems,
    };
  }

  // RM recommendations section
  if (processedData.rmRecs?.hasOpenRecommendations) {
    payload.sections.rmRecs = {
      hasRecommendations: true,
      summary: processedData.rmRecs.formattedSummary,
    };
  }

  // Groups section
  if (processedData.groups?.hasGroupsWithRooms) {
    payload.sections.groups = {
      hasGroups: true,
      summaryItems: processedData.groups.formattedSummaryItems,
    };
  }

  // New groups section
  if (processedData.newGroups?.hasNewGroups) {
    payload.sections.newGroups = {
      hasNewGroups: true,
      summaryItems: processedData.newGroups.formattedSummaryItems,
    };
  }

  return payload;
}
